import React from "react";
import {FaCar, FaClock, FaUser} from "react-icons/fa";

const JobCardDetailsModal = ({ show, handleClose, selectedJobCard }) => {
    return (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity ${
                show ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
        >
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={handleClose}
            ></div>

            {/* Modal */}
            <div
                className={`bg-white rounded-xl shadow-2xl w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 z-50 transform transition-all ${
                    show ? "scale-100" : "scale-95"
                } max-h-[90vh] flex flex-col`}
            >
                {/* Header */}
                <div className="bg-teal-400 text-white p-3 rounded-t-lg flex justify-between items-center">
                    <h2 className="text-2xl font-bold">Job Card Details</h2>
                    <button
                        onClick={handleClose}
                        className="text-white hover:text-gray-200 transition-colors"
                    >
                        &times;
                    </button>
                </div>

                {/* Scrollable Body */}
                <div className="p-6 space-y-6 overflow-y-auto flex-1">
                    {selectedJobCard ? (
                        <>
                            {/* Customer and Vehicle Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="flex items-center space-x-3">
                                        <FaUser className="text-blue-500 text-xl" />
                                        <div>
                                            <p className="text-sm text-gray-500">Customer</p>
                                            <p className="font-semibold">
                                                {selectedJobCard.customer?.name || "N/A"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <FaCar className="text-gray-500 text-xl" />
                                        <div>
                                            <p className="text-sm text-gray-500">Vehicle</p>
                                            <p className="font-semibold">
                                                {selectedJobCard.vehicleNumber || "N/A"}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Time Details */}
                                <div className="space-y-4">
                                    <div className="flex items-center space-x-3">
                                        <FaClock className="text-yellow-500 text-xl" />
                                        <div>
                                            <p className="text-sm text-gray-500">Estimated Time</p>
                                            <p className="font-semibold">
                                                {selectedJobCard.estimatedHours || "N/A"} hours
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <FaClock className="text-gray-500 text-xl" />
                                        <div>
                                            <p className="text-sm text-gray-500">Start Time</p>
                                            <p className="font-semibold">
                                                {selectedJobCard.startTime || "Not Started"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <FaClock className="text-gray-500 text-xl" />
                                        <div>
                                            <p className="text-sm text-gray-500">End Time</p>
                                            <p className="font-semibold">
                                                {selectedJobCard.endTime || "N/A"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <FaClock className="text-yellow-500 text-xl" />
                                        <div>
                                            <p className="text-sm text-gray-500">Total Time Taken</p>
                                            <p className="font-semibold">
                                                {selectedJobCard.totalTimeTaken || "N/A"} Hours
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Work Types */}
                            <div>
                                <h3 className="text-xl font-semibold text-blue-600 mb-4">Work Types</h3>
                                {selectedJobCard?.workTypes?.length > 0 ? (
                                    <div className="overflow-x-auto rounded-lg shadow-sm">
                                        <table className="min-w-full bg-white">
                                            <thead className="bg-gray-100">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">#</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Description</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Price</th>
                                            </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                            {selectedJobCard?.workTypes?.map((workType, index) => (
                                                <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-4 py-3 text-sm text-gray-700">{index + 1}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">{workType.name}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">{workType.description}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">&#8377; {workType.price}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-center text-gray-500">No work types available.</p>
                                )}
                            </div>

                            {/* Assigned Employees */}
                            <div>
                                <h3 className="text-xl font-semibold text-blue-600 mb-4">Assigned Employees</h3>
                                {selectedJobCard?.assignedEmployees?.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {selectedJobCard?.assignedEmployees.map((employee, index) => (
                                            <span
                                                key={index}
                                                className="bg-green-100 text-green-800 px-3 py-1.5 rounded-full text-sm"
                                            >
                                                {employee.name}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-center text-gray-500">No employees assigned.</p>
                                )}
                            </div>

                            {/* Spare Parts */}
                            <div>
                                <h3 className="text-xl font-semibold text-blue-600 mb-4">Spare Parts</h3>
                                {selectedJobCard?.inventoryItems?.length > 0 ? (
                                    <div className="overflow-x-auto rounded-lg shadow-sm">
                                        <table className="min-w-full bg-white">
                                            <thead className="bg-gray-100">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">#</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Part Name</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Quantity</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Under Warranty</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Price</th>
                                            </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                            {selectedJobCard?.inventoryItems?.map((item, index) => (
                                                <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-4 py-3 text-sm text-gray-700">{index + 1}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">{item.name}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">{item.quantity}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">
                                                        {item.underWarranty ? (
                                                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                                                                    Yes
                                                                </span>
                                                        ) : (
                                                            <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">
                                                                    No
                                                                </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">{item.price}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-center text-gray-500">No spare parts used.</p>
                                )}
                            </div>
                        </>
                    ) : (
                        <p className="text-center text-gray-500">Loading details...</p>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t">
                    <button
                        onClick={handleClose}
                        className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default JobCardDetailsModal;